import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ITicket } from "../types/TicketTypes";
import { getAllTickets } from "../requests/AdminRequests";

interface ITicketState {
  openTickets: ITicket[];
  closeTickets: ITicket[];
  inProcessTickets: ITicket[];
  byProjectTickets: ITicket[];
  isLoading: boolean;
  error: string;

  searchTickets: ITicket[];
  searchTicketsLoading: boolean;
  searchTicketsError: string;

  closeTicketsTotal: number;
  closeTicketsPage: number;
  closeTicketsLimit: number;

  userTickets: ITicket[];
  userTicketsLoading: boolean;
  userTicketsError: string;

  historyMessages: any[];
  historyMessagesLoading: boolean;
  historyMessagesError: string;
}

const initialState: ITicketState = {
  openTickets: [],
  closeTickets: [],
  inProcessTickets: [],
  byProjectTickets: [],
  error: "",
  isLoading: false,

  closeTicketsTotal: 0,
  closeTicketsPage: 1,
  closeTicketsLimit: 50,

  searchTickets: [],
  searchTicketsLoading: false,
  searchTicketsError: "",

  userTickets: [],
  userTicketsLoading: false,
  userTicketsError: "",

  historyMessages: [],
  historyMessagesLoading: false,
  historyMessagesError: "",
};

type TicketListResponse = {
  items: ITicket[];
  total: number;
  page: number;
  limit: number;
};

export const ticketSlice = createSlice({
  name: "ticket",
  initialState,
  reducers: {
    openTicketsFetchSuccess(state, action: PayloadAction<ITicket[]>) {
      state.openTickets = action.payload;
      state.error = "";
      state.isLoading = false;
    },

    openTicketsFetchLoading(state) {
      state.error = "";
      state.isLoading = true;
    },

    openTicketsFetchFailed(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoading = false;
    },

    addOpenTicketSuccess(state, action: PayloadAction<ITicket>) {
      state.error = "";
      state.isLoading = false;
      state.openTickets.push(action.payload);
    },

    removeOpenTicketSuccess(state, action: PayloadAction<number>) {
      state.openTickets = state.openTickets.filter(
        (item) => item.id !== action.payload,
      );
    },

    addInProcessTicketLoading(state) {
      state.error = "";
      state.isLoading = true;
    },

    addInProcessTicketSuccess(state, action: PayloadAction<ITicket>) {
      state.error = "";
      state.isLoading = false;
      state.inProcessTickets.push(action.payload);
    },

    addInProcessTicketFailed(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoading = false;
    },

    getAllInProcessTicketsLoading(state) {
      state.error = "";
      state.isLoading = true;
    },
    getAllInProcessTicketsSuccess(state, action: PayloadAction<ITicket[]>) {
      state.error = "";
      state.isLoading = false;
      state.inProcessTickets = action.payload;
    },
    getAllInProcessTicketsFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getAllTicketsLoading(state) {
      state.error = "";
      state.isLoading = true;
    },

    getAllTicketsSuccess(state, action: PayloadAction<ITicket[]>) {
      state.error = "";
      state.isLoading = false;
      state.inProcessTickets = action.payload;
    },

    getAllTicketsFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    removeInProcessTicketLoading(state) {
      state.error = "";
      state.isLoading = true;
    },

    removeInProcessTicketSuccess(state, action: PayloadAction<number>) {
      state.error = "";
      state.isLoading = false;
      state.inProcessTickets = state.inProcessTickets.filter(
        (item) => item.id !== action.payload,
      );
    },

    removeInProcessTicketFailed(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoading = false;
    },

    getAllCloseTicketsLoading(state) {
      state.error = "";
      state.isLoading = true;
    },

    getAllCloseTicketsSuccess(state, action: PayloadAction<ITicket[]>) {
      state.closeTickets = action.payload;
      state.error = "";
      state.isLoading = false;
    },

    getAllCloseTicketsFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    setLastUserMessageTime(
      state,
      action: PayloadAction<{ ticketId: number; lastUserMessageTime: string }>,
    ) {
      const index = state.inProcessTickets.findIndex(
        (item) => item.id === action.payload.ticketId,
      );
      if (index !== -1) {
        state.inProcessTickets[index].lastUserMessageAt =
          action.payload.lastUserMessageTime;
      }
    },

    getAllOpenTicketsByProjectLoading(state) {
      state.error = "";
      state.isLoading = true;
    },

    getAllOpenTicketsByProjectSuccess(state, action: PayloadAction<ITicket[]>) {
      state.error = "";
      state.isLoading = false;
      state.byProjectTickets = action.payload;
    },

    getAllOpenTicketsByProjectFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    searchTicketsLoading(state) {
      state.searchTicketsLoading = true;
      state.searchTicketsError = "";
    },

    searchTicketsSuccess(state, action: PayloadAction<ITicket[]>) {
      state.searchTicketsLoading = false;
      state.searchTicketsError = "";
      state.searchTickets = action.payload;
    },

    searchTicketsFailed(state, action: PayloadAction<string>) {
      state.searchTicketsLoading = false;
      state.searchTicketsError = action.payload;
    },

    clearSearchTickets(state) {
      state.searchTickets = [];
      state.searchTicketsLoading = false;
      state.searchTicketsError = "";
    },

    getUserTicketsLoading(state) {
      state.userTicketsLoading = true;
      state.userTicketsError = "";
    },

    getUserTicketsSuccess(state, action: PayloadAction<ITicket[]>) {
      state.userTicketsLoading = false;
      state.userTicketsError = "";
      state.userTickets = action.payload;
    },

    getUserTicketsFailed(state, action: PayloadAction<string>) {
      state.userTicketsLoading = false;
      state.userTicketsError = action.payload;
    },

    clearUserTickets(state) {
      state.userTickets = [];
      state.userTicketsLoading = false;
      state.userTicketsError = "";
    },

    getHistoryMessagesLoading(state) {
      state.historyMessagesLoading = true;
      state.historyMessagesError = "";
    },

    getHistoryMessagesSuccess(state, action: PayloadAction<any[]>) {
      state.historyMessagesLoading = false;
      state.historyMessages = action.payload;
    },

    getHistoryMessagesFailed(state, action: PayloadAction<string>) {
      state.historyMessagesLoading = false;
      state.historyMessagesError = action.payload;
    },

    clearHistoryMessages(state) {
      state.historyMessages = [];
      state.historyMessagesLoading = false;
      state.historyMessagesError = "";
    },
  },
});

export default ticketSlice.reducer;
